import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Eye,
  EyeOff,
  Loader2,
  RefreshCcw,
  RotateCcw,
  Search,
  Trash2,
} from "lucide-react";
import {
  fetchRecycleList,
  type DeletedObject,
  type RecycleListResult,
} from "@/lib/recycleApi";
import {
  hideMany,
  hideOne,
  loadHidden,
  unhideOne,
} from "@/components/recycle/hideFilter";
import { RecycleDetailsDrawer } from "@/components/recycle/RecycleDetailsDrawer";
import { BulkRestoreModal } from "@/components/recycle/BulkRestoreModal";

// Empty string = no `deletedSince` parameter sent → backend returns
// every item in the recycle bin regardless of its `deleted_at` stamp.
// "All items" is the default because Cognos's per-account
// `_deletedObjectFolder` containers carry their CREATED-time MODIFIED
// timestamp (often years old), so a fixed window like P30D silently
// hides genuinely recoverable items. Recycle bins should "show what's
// there" the way every OS-native recycle bin does.
const WINDOW_OPTIONS: { label: string; value: string }[] = [
  { label: "All items", value: "" },
  { label: "Last 7 days", value: "P7D" },
  { label: "Last 30 days", value: "P30D" },
  { label: "Last 90 days", value: "P90D" },
  { label: "Last year", value: "P365D" },
];

/**
 * Top-level recycle-bin page (route: {@code /recycle}). Three layers:
 *
 * <ol>
 *   <li>List view with filter bar + checkbox table — the default.</li>
 *   <li>Details drawer (right side, slides in) when a row is clicked.</li>
 *   <li>Bulk-restore modal when the bottom-bar "Restore N" is fired.</li>
 * </ol>
 *
 * <p>"Hide" affordance is UI-only — localStorage filter, never calls
 * the server. Documented inline so operators don't expect permanent
 * delete from this surface.
 */
export function RecyclePage() {
  // Default to "" (all items) — see WINDOW_OPTIONS comment for why.
  const [windowSel, setWindowSel] = useState<string>("");
  const [scopePath, setScopePath] = useState<string>("");
  const [deletedBy, setDeletedBy] = useState<string>("");
  const [recoverableOnly, setRecoverableOnly] = useState<boolean | undefined>(undefined);
  const [search, setSearch] = useState<string>("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hidden, setHidden] = useState<Set<string>>(() => loadHidden());
  const [showHidden, setShowHidden] = useState<boolean>(false);
  const [drawerStoreId, setDrawerStoreId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState<boolean>(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery<RecycleListResult>({
    queryKey: ["recycle-list", windowSel, scopePath, deletedBy, recoverableOnly],
    queryFn: () =>
      fetchRecycleList({
        // Omit deletedSince when "All items" is selected — the backend
        // then applies no `deleted_at` filter and returns every row in
        // the bin (the OS-native recycle-bin default behaviour).
        deletedSince: windowSel || undefined,
        scopePath: scopePath || undefined,
        deletedBy: deletedBy || undefined,
        recoverableOnly,
        limit: 200,
      }),
    staleTime: 30_000,
  });

  // Local search + hide filter applied client-side over the server's
  // already-paginated result. Cheap for ~200 rows.
  const filtered = useMemo(() => {
    const objs = data?.objects ?? [];
    const term = search.trim().toLowerCase();
    return objs.filter((o) => {
      if (!showHidden && hidden.has(o.storeId)) return false;
      if (showHidden && !hidden.has(o.storeId)) return false;
      if (!term) return true;
      const hay = (
        (o.name ?? "") +
        " " +
        (o.objectClass ?? "") +
        " " +
        (o.displayPath ?? "") +
        " " +
        (o.deletedByUsername ?? "")
      ).toLowerCase();
      return hay.includes(term);
    });
  }, [data?.objects, search, hidden, showHidden]);

  // Clear selections on filter change (avoids stale selection of a now-hidden row).
  useEffect(() => {
    setSelected(new Set());
  }, [windowSel, scopePath, deletedBy, recoverableOnly, showHidden]);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((s) => {
      if (s.size === filtered.length && filtered.length > 0) return new Set();
      return new Set(filtered.map((o) => o.storeId));
    });
  }

  const selectedRows = useMemo(
    () => filtered.filter((o) => selected.has(o.storeId)),
    [filtered, selected],
  );

  function onHideOne(storeId: string) {
    setHidden(hideOne(storeId));
    setSelected((s) => {
      const n = new Set(s);
      n.delete(storeId);
      return n;
    });
  }

  function onHideMany() {
    setHidden(hideMany(Array.from(selected)));
    setSelected(new Set());
  }

  function onUnhide(storeId: string) {
    setHidden(unhideOne(storeId));
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-6 pb-24">
      <header className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-datumcraft-navy">
            Recycle bin
          </h1>
          <p className="mt-0.5 text-sm text-datumcraft-slate">
            Pick deleted items to restore. <span className="font-medium">Hide</span> is a
            view-only filter — it doesn't permanently delete; Cognos's own admin UI
            handles purge.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
        >
          {isFetching ? <Loader2 size={11} className="animate-spin" /> : <RefreshCcw size={11} />}
          Refresh
        </button>
      </header>

      {/* Filter bar */}
      <section
        data-tour-id="recycle-filters"
        className="mb-3 grid gap-2 rounded-md border border-datumcraft-slate/20 bg-white p-3 shadow-sm sm:grid-cols-2 lg:grid-cols-5"
      >
        <Field label="Search">
          <div className="relative">
            <Search
              size={11}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-datumcraft-slate"
              aria-hidden
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="name, path, user…"
              className="w-full rounded-md border border-datumcraft-slate/30 bg-white py-1 pl-7 pr-2 text-xs"
            />
          </div>
        </Field>
        <Field label="Scope path">
          <input
            type="text"
            value={scopePath}
            onChange={(e) => setScopePath(e.target.value)}
            placeholder="Public Folders/Finance"
            className="w-full rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-xs font-mono"
          />
        </Field>
        <Field label="Deleted by">
          <input
            type="text"
            value={deletedBy}
            onChange={(e) => setDeletedBy(e.target.value)}
            placeholder="username substring"
            className="w-full rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-xs"
          />
        </Field>
        <Field label="Window">
          <select
            value={windowSel}
            onChange={(e) => setWindowSel(e.target.value)}
            className="w-full rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-xs"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Recoverability">
          <select
            value={recoverableOnly === undefined ? "default" : recoverableOnly ? "yes" : "no"}
            onChange={(e) =>
              setRecoverableOnly(
                e.target.value === "default" ? undefined : e.target.value === "yes",
              )
            }
            className="w-full rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-xs"
          >
            <option value="default">Use Settings default</option>
            <option value="yes">Recoverable only</option>
            <option value="no">All deleted (incl. past-retention)</option>
          </select>
        </Field>
      </section>

      {/* Hidden-toggle */}
      <div className="mb-2 flex items-center justify-between text-2xs text-datumcraft-slate">
        <div>
          {data && (
            <>
              Showing <span className="font-medium text-datumcraft-ink">{filtered.length}</span>{" "}
              of <span className="font-medium text-datumcraft-ink">{data.totalMatched}</span>
              {" "}— {data.windowDescription}
              {data.recoverableOnly && " · recoverable only"}
              {hidden.size > 0 && !showHidden && (
                <>
                  {" · "}
                  <button
                    type="button"
                    onClick={() => setShowHidden(true)}
                    className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
                  >
                    <Eye size={10} /> show {hidden.size} hidden
                  </button>
                </>
              )}
              {showHidden && (
                <>
                  {" · "}
                  <button
                    type="button"
                    onClick={() => setShowHidden(false)}
                    className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
                  >
                    <EyeOff size={10} /> back to active
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* List */}
      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-datumcraft-slate">
          <Loader2 size={12} className="animate-spin" /> Loading recycle bin…
        </div>
      )}
      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {(error as Error).message}
        </p>
      )}
      {data && filtered.length === 0 && (
        <div className="rounded-md border border-dashed border-datumcraft-slate/30 bg-white p-8 text-center text-sm text-datumcraft-slate">
          {showHidden ? "Nothing hidden." : "Recycle bin is empty (or filter excluded everything)."}
        </div>
      )}
      {filtered.length > 0 && (
        <div data-tour-id="recycle-table" className="overflow-hidden rounded-md border border-datumcraft-slate/20 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-datumcraft-mist/50 text-2xs uppercase tracking-wider text-datumcraft-slate">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={
                      selected.size > 0 && selected.size === filtered.length
                    }
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">Original parent</th>
                <th className="px-3 py-2 text-left font-medium">Deleted by</th>
                <th className="px-3 py-2 text-left font-medium">When</th>
                <th className="px-3 py-2 text-left font-medium">State</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <RecycleRow
                  key={o.storeId}
                  obj={o}
                  selected={selected.has(o.storeId)}
                  hidden={hidden.has(o.storeId)}
                  showHidden={showHidden}
                  onToggle={() => toggle(o.storeId)}
                  onOpen={() => setDrawerStoreId(o.storeId)}
                  onHide={() => onHideOne(o.storeId)}
                  onUnhide={() => onUnhide(o.storeId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bulk action bar (anchored, only when selection exists) */}
      {selected.size > 0 && !showHidden && (
        <div
          data-tour-id="recycle-bulk-bar"
          className="fixed inset-x-0 bottom-0 z-30 border-t border-datumcraft-slate/30 bg-white/95 backdrop-blur"
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-6 py-3">
            <div className="text-sm text-datumcraft-ink">
              <span className="font-semibold">{selected.size}</span> item
              {selected.size === 1 ? "" : "s"} selected
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onHideMany}
                className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
                title="UI-only filter; doesn't delete"
              >
                <EyeOff size={11} /> Hide selected
              </button>
              <button
                type="button"
                onClick={() => setBulkOpen(true)}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700"
              >
                <RotateCcw size={11} /> Restore {selected.size}
              </button>
            </div>
          </div>
        </div>
      )}

      <RecycleDetailsDrawer
        storeId={drawerStoreId}
        onClose={() => setDrawerStoreId(null)}
        onRestored={() => {
          setSelected(new Set());
        }}
      />

      <BulkRestoreModal
        open={bulkOpen}
        selected={selectedRows}
        onClose={() => setBulkOpen(false)}
        onComplete={() => {
          setSelected(new Set());
          // Modal stays open with the receipt; user clicks Done to dismiss.
        }}
      />
    </div>
  );
}

function RecycleRow({
  obj,
  selected,
  hidden,
  showHidden,
  onToggle,
  onOpen,
  onHide,
  onUnhide,
}: {
  obj: DeletedObject;
  selected: boolean;
  hidden: boolean;
  showHidden: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onHide: () => void;
  onUnhide: () => void;
}) {
  const recoverable = obj.recoverability === "recoverable";
  return (
    <tr
      className={`border-t border-datumcraft-slate/15 ${selected ? "bg-emerald-50/40" : ""}`}
    >
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${obj.name ?? obj.storeId}`}
        />
      </td>
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={onOpen}
          className="text-left font-medium text-datumcraft-navy hover:underline"
        >
          {obj.name ?? "—"}
        </button>
        <div className="font-mono text-2xs text-datumcraft-slate">{obj.storeId}</div>
      </td>
      <td className="px-3 py-2 text-2xs text-datumcraft-slate">{obj.objectClass ?? "—"}</td>
      <td className="px-3 py-2 font-mono text-2xs text-datumcraft-ink">
        {obj.originalParentDisplayPath ?? "—"}
      </td>
      <td className="px-3 py-2 text-xs text-datumcraft-ink">{obj.deletedByUsername ?? "—"}</td>
      <td className="px-3 py-2 text-2xs text-datumcraft-slate">
        {obj.deletedAt ? new Date(obj.deletedAt).toLocaleString() : "—"}
      </td>
      <td className="px-3 py-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
            recoverable
              ? "bg-emerald-100 text-emerald-800"
              : "bg-amber-100 text-amber-800"
          }`}
          title={obj.recoverability}
        >
          {recoverable ? "Recoverable" : "Past retention"}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        {showHidden && hidden ? (
          <button
            type="button"
            onClick={onUnhide}
            className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-2xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
            title="Restore to view"
          >
            <Eye size={10} /> Unhide
          </button>
        ) : (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={onOpen}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-2xs font-medium text-white hover:bg-emerald-700"
            >
              <RotateCcw size={10} /> Inspect / Restore
            </button>
            <button
              type="button"
              onClick={onHide}
              className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-2xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
              title="Hide from this list (UI-only filter)"
            >
              <Trash2 size={10} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-2xs font-medium uppercase tracking-wider text-datumcraft-slate">
        {label}
      </span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}
